import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { Alert, Button, Spinner } from './index';

/* ---------- Paged list hook ---------- */
export interface Paged<T> { items: T[]; page: number; pageSize: number; totalCount: number; totalPages: number }

export interface ListState<T> {
  data: Paged<T> | null;
  loading: boolean;
  error: string | null;
  page: number;
  search: string;
  setPage: (p: number) => void;
  setSearch: (s: string) => void;
  reload: () => void;
}

/** Fetches `path` with page/pageSize/search + extra query params; re-fetches when any of them change. */
export function usePagedList<T>(path: string, extra: Record<string, string | number | boolean | undefined | null> = {}, pageSize = 25): ListState<T> {
  const [data, setData] = useState<Paged<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearchState] = useState('');
  const [tick, setTick] = useState(0);
  const extraKey = JSON.stringify(extra);
  const debounce = useRef<number | undefined>(undefined);

  const setSearch = useCallback((s: string) => {
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => { setSearchState(s); setPage(1); }, 300);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true); setError(null);
    api<Paged<T>>(path, { query: { page, pageSize, search: search || undefined, ...JSON.parse(extraKey) }, signal: ctrl.signal })
      .then(setData)
      .catch((e) => { if (!ctrl.signal.aborted) setError(e instanceof ApiError ? e.message : 'Failed to load'); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [path, page, pageSize, search, extraKey, tick]);

  return { data, loading, error, page, search, setPage, setSearch, reload: () => setTick((t) => t + 1) };
}

/** Fetch a single resource (or small list) once; `deps` re-trigger. */
export function useFetch<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) { setData(null); setLoading(false); return; }
    const ctrl = new AbortController();
    setLoading(true); setError(null);
    api<T>(path, { signal: ctrl.signal })
      .then(setData)
      .catch((e) => { if (!ctrl.signal.aborted) setError(e instanceof ApiError ? e.message : 'Failed to load'); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps]);
  return { data, loading, error, reload: () => setTick((t) => t + 1), setData };
}

/* ---------- Table ---------- */
export interface Column<T> { key: string; header: ReactNode; render: (row: T) => ReactNode; width?: string | number; align?: 'start' | 'end' }

export function DataTable<T>({ columns, rows, rowKey, onRowClick, empty }: {
  columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string; onRowClick?: (row: T) => void; empty?: ReactNode;
}) {
  if (rows.length === 0) return <p className="gr-muted" style={{ padding: 16 }}>{empty ?? 'No records.'}</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="gr-table">
        <thead><tr>{columns.map((c) => <th key={c.key} style={{ width: c.width, textAlign: c.align }}>{c.header}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined} style={{ cursor: onRowClick ? 'pointer' : undefined }}>
              {columns.map((c) => <td key={c.key} style={{ textAlign: c.align }}>{c.render(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="gr-row" style={{ justifyContent: 'flex-end', padding: '12px 0 0' }}>
      <Button variant="outline" style={{ minHeight: 36 }} disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</Button>
      <span className="gr-muted" style={{ fontSize: 14 }}>{page} / {totalPages}</span>
      <Button variant="outline" style={{ minHeight: 36 }} disabled={page >= totalPages} onClick={() => onChange(page + 1)}>›</Button>
    </div>
  );
}

/** List page chrome: title, search box, actions, table, pagination, loading/error states. */
export function ListPage<T>({ title, state, columns, rowKey, onRowClick, actions, filters, empty }: {
  title: ReactNode; state: ListState<T>; columns: Column<T>[]; rowKey: (row: T) => string; onRowClick?: (row: T) => void;
  actions?: ReactNode; filters?: ReactNode; empty?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>{title}</h1>
        <div className="gr-row">{actions}</div>
      </div>
      <div className="gr-row gr-filters">
        <input className="gr-input" style={{ maxWidth: 320, minHeight: 40 }} placeholder={t('common.search')} defaultValue={state.search} onChange={(e) => state.setSearch(e.target.value)} />
        {filters}
      </div>
      <div className="gr-card gr-card--sm" style={{ padding: 0 }}>
        {state.error && <div style={{ padding: 16 }}><Alert tone="error">{state.error}</Alert></div>}
        {state.loading && !state.data ? <div style={{ padding: 24 }}><Spinner /></div>
          : state.data && <DataTable columns={columns} rows={state.data.items} rowKey={rowKey} onRowClick={onRowClick} empty={empty} />}
      </div>
      {state.data && <Pagination page={state.page} totalPages={state.data.totalPages} onChange={state.setPage} />}
    </div>
  );
}

/* ---------- Modal / drawer ---------- */
export function Modal({ open, title, onClose, children, wide }: { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="gr-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`gr-modal ${wide ? 'gr-modal--wide' : ''}`} role="dialog" aria-modal="true">
        <div className="gr-modal__head"><h2 style={{ margin: 0 }}>{title}</h2><button className="gr-btn gr-btn--ghost" style={{ minHeight: 36 }} onClick={onClose} aria-label="Close">✕</button></div>
        <div className="gr-modal__body">{children}</div>
      </div>
    </div>
  );
}

/* ---------- Form helpers ---------- */
export function useForm<T extends Record<string, any>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const set = <K extends keyof T>(key: K, value: T[K]) => setValues((v) => ({ ...v, [key]: value }));
  const bind = <K extends keyof T>(key: K) => ({
    value: (values[key] ?? '') as any,
    onChange: (e: { target: { value: string; type?: string; checked?: boolean } }) =>
      set(key, (e.target.type === 'checkbox' ? e.target.checked : e.target.value) as T[K]),
    error: errors[key as string]?.[0],
  });
  const submit = async (fn: (values: T) => Promise<void>) => {
    setBusy(true); setErrors({}); setMessage(null);
    try { await fn(values); return true; }
    catch (e) {
      if (e instanceof ApiError) { setErrors(e.fieldErrors); setMessage(e.message); }
      else setMessage((e as Error).message ?? 'Failed');
      return false;
    } finally { setBusy(false); }
  };
  return { values, setValues, set, bind, errors, busy, message, submit, reset: (v: T) => { setValues(v); setErrors({}); setMessage(null); } };
}

export function fmtDate(iso?: string | null, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }) {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat(undefined, opts).format(new Date(iso)); } catch { return iso; }
}

export function fmtMoney(minor: number, currency: string) {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / 100); } catch { return `${(minor / 100).toFixed(2)} ${currency}`; }
}
